'use client';

import type React from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useConsent } from '../../contexts/ConsentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePlayer } from '../../contexts/PlayerContext';
import { unlockAudioContext } from '../SoundCloudPlayer';

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(4, 4, 8, 0.82);
  backdrop-filter: blur(8px);
  z-index: 10000;
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${slideUp} 0.3s ease;
`;

const Modal = styled.div`
  background: rgba(14, 14, 24, 0.98);
  border: 1px solid rgba(0, 255, 0, 0.3);
  box-shadow:
    0 0 40px rgba(0, 0, 0, 0.9),
    0 0 30px rgba(0, 255, 0, 0.1);
  border-radius: 18px;
  max-width: 560px;
  width: 100%;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #fff;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 480px) {
    padding: 20px;
    gap: 14px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Tag = styled.span`
  font-size: 0.7rem;
  color: #ff2d95;
  font-variant-caps: all-petite-caps;
  letter-spacing: 2px;
  font-weight: 700;
`;

const Title = styled.h2`
  font-size: 1.4rem;
  margin: 0;
  font-variant-caps: all-petite-caps;
  color: #fff;
  font-weight: 500;
`;

const Description = styled.p`
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin: 0;
`;

const TrackHighlight = styled.div`
  background: rgba(0, 255, 0, 0.08);
  border: 1px solid rgba(0, 255, 0, 0.25);
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #00ff00;
    flex-shrink: 0;
  }

  span {
    font-size: 0.9rem;
    color: #fff;
    font-weight: 500;
  }
`;

const CustomizeToggle = styled.button`
  background: none;
  border: none;
  color: #00ff00;
  font-size: 0.8rem;
  font-variant-caps: all-petite-caps;
  letter-spacing: 1px;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: underline;
  transition: color 0.2s;

  &:hover {
    color: #ff2d95;
  }
`;

const PreferencesSection = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  flex-direction: column;
  gap: 12px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  margin-top: 4px;
`;

const PreferenceItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 0.85rem;

  input[type='checkbox'] {
    accent-color: #00ff00;
    width: 18px;
    height: 18px;
    margin-top: 2px;
    cursor: pointer;
  }
`;

const PreferenceText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    color: #fff;
    font-size: 0.85rem;
  }

  span {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.78rem;
    line-height: 1.4;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  flex: 1;
  min-width: 180px;
  background: #00ff00;
  color: #000;
  border: 1px solid #00ff00;
  padding: 12px 18px;
  border-radius: 24px;
  font-size: 0.85rem;
  font-variant-caps: all-petite-caps;
  font-weight: 700;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    background: #1aff1a;
    transform: scale(1.02);
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.4);
  }
`;

const SaveBtn = styled.button`
  background: rgba(0, 255, 0, 0.15);
  color: #00ff00;
  border: 1px solid #00ff00;
  padding: 12px 18px;
  border-radius: 24px;
  font-size: 0.85rem;
  font-variant-caps: all-petite-caps;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #00ff00;
    color: #000;
  }
`;

const SecondaryBtn = styled.button`
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.25);
  padding: 12px 16px;
  border-radius: 24px;
  font-size: 0.85rem;
  font-variant-caps: all-petite-caps;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #ff2d95;
    color: #fff;
    background: rgba(255, 45, 149, 0.1);
  }
`;

const PolicyLink = styled(Link)`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  text-decoration: underline;
  text-align: center;
  margin-top: 4px;
  transition: color 0.2s;

  &:hover {
    color: #00ff00;
  }
`;

export const ConsentBanner: React.FC = () => {
  const {
    isBannerOpen,
    isSoundCloudPromptOpen,
    pendingTrackInfo,
    preferences,
    acceptAll,
    acceptEssential,
    savePreferences,
    confirmSoundCloudPermission,
    cancelSoundCloudPrompt,
  } = useConsent();

  const { playTrack, currentTrackUrl, isPlaying } = usePlayer();
  const { t } = useLanguage();

  const [showCustomize, setShowCustomize] = useState(false);
  const [selectedSC, setSelectedSC] = useState(preferences.soundCloud);
  const [selectedYT, setSelectedYT] = useState(preferences.youtube);

  useEffect(() => {
    setSelectedSC(preferences.soundCloud);
    setSelectedYT(preferences.youtube);
  }, [preferences]);

  // Handle re-requesting permission specifically for SoundCloud playback
  if (isSoundCloudPromptOpen) {
    const handleConfirmSC = () => {
      unlockAudioContext();
      confirmSoundCloudPermission();
      if (pendingTrackInfo?.url) {
        playTrack(pendingTrackInfo.url, pendingTrackInfo.title);
      } else if (!isPlaying && !currentTrackUrl) {
        playTrack(
          'https://soundcloud.com/joevnoise/tough-love-joevnoise-sailorandi-tribute',
          'Tough Love (Sailor & I Tribute)'
        );
      }
    };

    return (
      <Overlay $visible={true} role="dialog" aria-modal="true" aria-labelledby="sc-prompt-title">
        <Modal>
          <Header>
            <Tag>{t('scPromptTag')}</Tag>
          </Header>
          <Title id="sc-prompt-title">{t('scPromptTitle')}</Title>
          <Description>{t('scPromptDescription')}</Description>

          {pendingTrackInfo?.title && (
            <TrackHighlight>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
              </svg>
              <span>{pendingTrackInfo.title}</span>
            </TrackHighlight>
          )}

          <ButtonGroup>
            <PrimaryBtn onClick={handleConfirmSC} aria-label={t('scPromptAllowBtn')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5,3 19,12 5,21" />
              </svg>
              {t('scPromptAllowBtn')}
            </PrimaryBtn>
            <SecondaryBtn onClick={cancelSoundCloudPrompt} aria-label={t('scPromptCancelBtn')}>
              {t('scPromptCancelBtn')}
            </SecondaryBtn>
          </ButtonGroup>

          <PolicyLink to="/privacy">{t('consentPrivacyLink')}</PolicyLink>
        </Modal>
      </Overlay>
    );
  }

  if (!isBannerOpen) return null;

  const handleAcceptAll = () => {
    unlockAudioContext();
    acceptAll();
    if (!isPlaying && !currentTrackUrl) {
      playTrack(
        'https://soundcloud.com/joevnoise/tough-love-joevnoise-sailorandi-tribute',
        'Tough Love (Sailor & I Tribute)'
      );
    }
  };

  const handleSavePreferences = () => {
    if (selectedSC) {
      unlockAudioContext();
    }
    savePreferences({
      soundCloud: selectedSC,
      youtube: selectedYT,
    });
    if (selectedSC && !isPlaying && !currentTrackUrl) {
      playTrack(
        'https://soundcloud.com/joevnoise/tough-love-joevnoise-sailorandi-tribute',
        'Tough Love (Sailor & I Tribute)'
      );
    }
  };

  return (
    <Overlay
      $visible={isBannerOpen}
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-title"
    >
      <Modal>
        <Header>
          <Tag>{t('consentTag')}</Tag>
        </Header>
        <Title id="consent-title">{t('consentTitle')}</Title>
        <Description>{t('consentDescription')}</Description>

        <div>
          <CustomizeToggle
            type="button"
            onClick={() => setShowCustomize((prev) => !prev)}
            aria-expanded={showCustomize}
          >
            {showCustomize ? t('consentCustomizeHide') : t('consentCustomizeShow')}
          </CustomizeToggle>
        </div>

        <PreferencesSection $visible={showCustomize}>
          <PreferenceItem>
            <input type="checkbox" checked disabled readOnly />
            <PreferenceText>
              <strong>{t('consentEssentialTitle')}</strong>
              <span>{t('consentEssentialDesc')}</span>
            </PreferenceText>
          </PreferenceItem>

          <PreferenceItem>
            <input
              type="checkbox"
              checked={selectedSC}
              onChange={(e) => setSelectedSC(e.target.checked)}
            />
            <PreferenceText>
              <strong>{t('consentSoundCloudTitle')}</strong>
              <span>{t('consentSoundCloudDesc')}</span>
            </PreferenceText>
          </PreferenceItem>

          <PreferenceItem>
            <input
              type="checkbox"
              checked={selectedYT}
              onChange={(e) => setSelectedYT(e.target.checked)}
            />
            <PreferenceText>
              <strong>{t('consentYouTubeTitle')}</strong>
              <span>{t('consentYouTubeDesc')}</span>
            </PreferenceText>
          </PreferenceItem>
        </PreferencesSection>

        <ButtonGroup>
          <PrimaryBtn onClick={handleAcceptAll} aria-label={t('consentAcceptAll')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 19,12 5,21" />
            </svg>
            {t('consentAcceptAll')}
          </PrimaryBtn>

          {showCustomize && (
            <SaveBtn onClick={handleSavePreferences} aria-label={t('consentSaveSelection')}>
              {t('consentSaveSelection')}
            </SaveBtn>
          )}

          <SecondaryBtn onClick={acceptEssential} aria-label={t('consentEssentialOnly')}>
            {t('consentEssentialOnly')}
          </SecondaryBtn>
        </ButtonGroup>

        <PolicyLink to="/privacy">{t('consentPrivacyLink')}</PolicyLink>
      </Modal>
    </Overlay>
  );
};

export default ConsentBanner;
